import { useQuery } from '@tanstack/react-query';
import { OFT_ABI } from '../config/oft';
import { DEPOSIT_CHAIN_BY_ID, INK_DEPOSIT_CHAIN } from '../config/chains';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';
import { buildExtraOptions } from '../utils/buildExtraOptions';

export interface OftQuoteOFTData {
  /** Amount sent from source chain in local decimals */
  amountSentLD: bigint;
  /** Amount received on destination after dust removal, in local decimals */
  amountReceivedLD: bigint;
}

/**
 * Calls `quoteOFT()` on the source chain's OFT contract to get dust-adjusted amounts.
 *
 * The `amountReceivedLD` is the minimum amount after LayerZero dust removal,
 * and should be used as `minAmountLD` in the actual `send()` call.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 * @param amount - Amount to send in token's local decimals (6 for USDT0)
 */
export function useOftQuoteOFT(
  sourceChainId: number | undefined,
  amount: bigint | undefined,
) {
  return useQuery<OftQuoteOFTData>({
    queryKey: ['oftQuoteOFT', sourceChainId, amount?.toString()],
    queryFn: async () => {
      if (!sourceChainId || amount === undefined) {
        throw new Error('sourceChainId and amount are required');
      }

      const chainConfig = DEPOSIT_CHAIN_BY_ID[sourceChainId];
      if (!chainConfig) {
        throw new Error(`No chain config for chainId ${sourceChainId}`);
      }

      const publicClient = getSourceChainPublicClient(sourceChainId);
      const extraOptions = buildExtraOptions();

      const sendParam = {
        dstEid: INK_DEPOSIT_CHAIN.lzEndpointId,
        to: ('0x' + '0'.repeat(64)) as `0x${string}`, // zero address bytes32 for quote
        amountLD: amount,
        minAmountLD: BigInt("0"), // zero for quote — we want the full quote
        extraOptions,
        composeMsg: '0x' as `0x${string}`,
        oftCmd: '0x' as `0x${string}`,
      };

      const [, , oftReceipt] = await publicClient.readContract({
        address: chainConfig.oftAddress,
        abi: OFT_ABI,
        functionName: 'quoteOFT',
        args: [sendParam, false],
      });

      return {
        amountSentLD: oftReceipt.amountSentLD,
        amountReceivedLD: oftReceipt.amountReceivedLD,
      };
    },
    enabled: !!sourceChainId && amount !== undefined && amount > BigInt("0"),
    staleTime: 30_000,
  });
}
